<template>
  <div>
    <v-dialog v-model="dialog" width="unset">
      <v-card style="width: 500px" class="card-container">
        <v-container class="px-10 py-8">
          <p class="subtitle-1 font-weight-bold mb-4">Complaint Details</p>

          <v-row>
            <v-icon class="mr-2">mdi-card-bulleted</v-icon>
            <p class="title" style="line-height: 1">{{ title }}</p>
          </v-row>
          <v-row>
            <span class="caption ml-8"
              >Unit: <strong>{{ unitName }}</strong></span
            >
          </v-row>
          <v-row>
            <span class="caption ml-8"
              >Requester: <strong>{{ requester }}</strong></span
            >
          </v-row>
          <v-row class="mb-4">
            <span class="caption ml-8">Date Created: {{ dateCreated }}</span>
          </v-row>
          <v-row>
            <v-icon class="mr-2">mdi-progress-wrench</v-icon>
            <p class="subtitle-2 font-weight-bold">Status</p>
          </v-row>
          <v-row>
            <v-select
              v-model="status"
              :items="statuses"
              class="ml-8"
              label="Select Status"
              solo
              flat
              dense
              :disabled="!access.update"
            >
              <template v-slot:selection="{ item, disabled }">
                <p v-if="disabled" class="body-2 grey--text">{{ item.text }}</p>
                <p v-else :class="selectionClass(item.value)">
                  {{ item.text }}
                </p>
              </template>
              <template v-slot:item="{ item }">
                <p :class="selectionClass(item.value)">{{ item.text }}</p>
              </template>
            </v-select>
          </v-row>
          <v-row>
            <v-icon class="mr-2">mdi-segment</v-icon>
            <p class="subtitle-2 font-weight-bold">Description</p>
          </v-row>
          <v-row class="mb-4">
            <p class="caption ml-8" style="white-space: pre-line">
              {{ description }}
            </p>
          </v-row>

          <v-row>
            <v-icon class="mr-2">mdi-image-multiple</v-icon>
            <p class="subtitle-2 font-weight-bold">Attachments</p>
          </v-row>
          <!-- <v-row> -->
          <swiper
            v-if="images.length"
            ref="mySwiper"
            class="mt-2 mb-6 ml-5"
            :options="swiperOptions"
          >
            <swiper-slide v-for="(pic, index) in images" :key="index">
              <v-avatar color="white" tile="tile" size="150px" class="pa-1">
                <v-img :src="pic" contain @click="openPicDialog(pic)"></v-img>
              </v-avatar>
            </swiper-slide>
            <div slot="pagination" class="swiper-pagination"></div>
          </swiper>
          <div v-else class="my-6"></div>
          <!-- </v-row> -->
          <v-row>
            <v-col>
              <v-row>
                <v-icon class="mr-2">mdi-calendar-month</v-icon>
                <p class="subtitle-2 font-weight-bold">Due date</p>
              </v-row>
              <v-row class="my-2">
                <v-menu
                  v-model="dueDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDate(dueDate)"
                      class="ml-8"
                      v-bind="attrs"
                      hide-details
                      solo
                      flat
                      dense
                      v-on="on"
                    >
                    </v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dueDate"
                    no-title
                    scrollable
                    @input="dueDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-row>
            </v-col>
          </v-row>

          <v-row class="mb-2">
            <v-icon class="mr-2">mdi-comment-text</v-icon>
            <p class="subtitle-2 font-weight-bold">Activity</p>
          </v-row>
          <v-row>
            <div class="d-flex flex-row" style="width: 100%">
              <v-avatar size="28px" color="grey" class="mt-1 mr-3">
                <v-img
                  v-if="user.profilePicture"
                  :src="user.profilePicture"
                ></v-img>
                <v-icon v-else dark small>mdi-account</v-icon>
              </v-avatar>
              <v-text-field
                v-model="comment"
                solo
                dense
                flat
                @keydown.enter="addNote"
              >
                <template v-slot:label>
                  <span class="caption grey--text text-lighten-1"
                    >Write a comment...</span
                  >
                </template>
                <template v-slot:append>
                  <v-icon small @click="addNote">mdi-send</v-icon>
                </template>
              </v-text-field>
            </div>
          </v-row>

          <v-row
            v-for="(item, index) in notes.slice().reverse()"
            :key="index"
            class="mb-4"
          >
            <v-avatar size="28px" color="grey" class="my-1 mr-3">
              <v-img
                v-if="note_details[notes.length - index - 1].profilePicture"
                :src="note_details[notes.length - index - 1].profilePicture"
              ></v-img>
              <v-icon v-else dark small>mdi-account</v-icon>
            </v-avatar>
            <div class="d-flex flex-column flex-grow-1">
              <div class="d-flex flex-row mb-1">
                <p class="caption font-weight-bold mr-2">
                  {{ getName(note_details[notes.length - index - 1]) }}
                </p>
                <span class="caption">{{ parseDate(item.date) }}</span>
              </div>
              <v-card flat class="pa-2 px-3" style="width: 100%">
                <p class="caption">{{ item.text }}</p>
              </v-card>
            </div>
          </v-row>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="font-weight-bold"
              color="primary"
              depressed
              @click="save"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-container>
      </v-card>
    </v-dialog>
    <v-dialog v-model="imageDialog" width="unset" @keydown.esc="cancel">
      <v-card max-width="600px" max-height="600px">
        <v-img :src="selectedPic" max-height="500px" contain></v-img>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snackbar">
      {{ snackbarMessage }}
      <v-btn text color="primary" @click.native="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { Swiper, SwiperSlide, directive } from 'vue-awesome-swiper'
import 'swiper/css/swiper.css'
import { dayjs } from '../../util/date'

export default {
  components: {
    Swiper,
    SwiperSlide
  },
  directives: {
    swiper: directive
  },
  data() {
    return {
      dialog: false,
      complaintId: -1,
      requester: '',
      unitName: '',
      title: '',
      description: '',
      images: [],
      status: '',
      dateCreated: '',
      swiperOptions: {
        slidesPerView: 3,
        slidesPerGroup: 3,
        spaceBetween: 10,
        slideClass: 'custom-slide-class',
        pagination: {
          el: '.swiper-pagination',
          clickable: true
        }
        // Some Swiper option/callback...
      },
      imageDialog: false,
      selectedPic: '',
      notes: [],
      note_details: [],
      statuses: [
        { value: 'pending', text: 'Pending' },
        { value: 'in_progress', text: 'In Progress' },
        { value: 'resolved', text: 'Resolved' },
        { value: 'rejected', text: 'Rejected' }
      ],
      comment: '',
      snackbar: false,
      snackbarMessage: '',
      refresh: null,
      dueDate: '',
      dueDateMenu: false
    }
  },
  computed: {
    swiper() {
      return this.$refs.mySwiper.$swiper
    },
    user() {
      return this.$store.getters['auth/getUser']
    },
    access() {
      return this.$root.$roleAccess('complaints').access
    }
  },
  methods: {
    async open(params, refresh) {
      const {
        unitName,
        complaintId,
        requester,
        complaintTitle,
        description,
        images,
        status,
        dateCreated,
        notes,
        note_details,
        dueDate
      } = params
      this.unitName = unitName
      this.complaintId = complaintId
      this.dialog = true
      this.requester = requester
      this.title = complaintTitle
      this.description = description
      this.images = images
      this.status = status
      this.dateCreated = dateCreated
      this.notes = notes
      this.note_details = note_details
      this.refresh = refresh
      this.dueDate = dueDate
    },
    openPicDialog(src) {
      this.selectedPic = src
      this.imageDialog = true
    },
    async addNote() {
      const comment = this.comment
      this.comment = ''
      this.notes.push({
        date: 'now',
        text: comment
      })
      this.note_details.push(this.user)
      this.$store
        .dispatch('complaints/addNoteAction', {
          complaintId: this.complaintId,
          note: comment
        })
        .catch((err) => {
          this.snackbarMessage = err
          this.snackbar = true
        })
    },
    getName(user) {
      let firstName = user.firstName ? user.firstName : ''
      let lastName = user.lastName ? user.lastName : ''

      if (!firstName || !lastName) return user.username

      return `${firstName} ${lastName}`
    },
    parseDate(date) {
      if (date == 'now') {
        return 'Just now'
      }
      return dayjs(date).format(`MMM D [at] h:mma`)
    },
    async updateStatus() {
      this.$store
        .dispatch('complaints/updateComplaintsStatusAction', {
          status: this.status,
          complaintId: this.complaintId,
          dueDate: dayjs(this.dueDate).format()
        })
        .then(() => {
          this.refresh()
        })
        .catch((err) => {
          this.snackbarMessage = err
          this.snackbar = true
        })
    },
    save() {
      this.updateStatus()
      this.dialog = false
    },
    selectionClass(value) {
      switch (value) {
        case 'pending':
          return 'body-2 red--text'
        case 'in_progress':
          return 'body-2 blue--text'
        case 'resolved':
          return 'body-2 green--text'
        case 'rejected':
          return 'body-2 grey--text'
      }
    },
    formatDate(date) {
      return dayjs(date).format('MMMM D, YYYY')
    }
  }
}
</script>

<style scoped>
.card-container {
  background-color: #f4f7f9;
}
</style>
